import React from 'react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import LegalLayout from '../components/LegalLayout';

const Privacy: React.FC = () => {
  return (
    <div>
      <SEO
        title="Privacy Policy"
        description="Learn how eTENANT collects, uses, and protects your personal data across the app and website, and the choices you have over your information."
        path="/privacy"
        breadcrumb="Privacy Policy"
      />

      {/* Page Header */}
      <PageHero
        crumb="Privacy Policy"
        title={<>Privacy Policy</>}
        subtitle="Last Updated: 10/12/2026"
      />

      {/* Privacy Content */}
      <LegalLayout>
        
        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">1. Introduction</h2>
          <p className="mb-3">
            Welcome to eTENANT.
          </p>
          <p className="mb-3">
            eTENANT is a cloud-based property and tenant management platform operated by <strong>INVOVANE COMPANY</strong>.
          </p>
          <p className="mb-3">
            Registered Address: 489 Shaw Boulevard, Mandaluyong City, Metro Manila, Philippines<br />
            Email: <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>
          </p>
          <p className="mb-3">
            eTENANT provides services for both Property Owners (managing rental properties and tenants) and Real Estate Agents (managing leads, properties, owners, tenants, rental agreements, and sales agreements on behalf of clients).
          </p>
          <p className="mb-3">
            This Privacy Policy describes how <strong>INVOVANE COMPANY</strong> collects, uses, and discloses information when you use our services at www.etenant.net.
          </p>
          <p>
            By using eTENANT, you agree to this Privacy Policy and our Terms and Conditions.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">2. Defined Terms</h2>
          <p className="mb-3">
            The term "SERVICE" refers to the www.etenant.net website, which is owned and maintained by INVOVANE COMPANY.
          </p>
          <p className="mb-3">
            PERSONAL DATA refers to information on a living human who can be identified based on the information (or from those and other information either in our possession or likely to come into our possession).
          </p>
          <p className="mb-3">
            USAGE DATA is data that is gathered automatically as a result of the use of the Service or as a result of the Service infrastructure itself (for example, the duration of a page visit).
          </p>
          <p className="mb-3">
            COOKIES are little files that your device stores on it (computer or mobile device).
          </p>
          <p className="mb-3">
            DATA CONTROLLER is a natural or legal person who, alone or jointly or in concert with other persons, determines the purposes and manner in which personal data are or will be processed. We are the Data Controller of your data for this Privacy Policy.
          </p>
          <p className="mb-3">
            DATA PROCESSORS (OR SERVICE PROVIDERS) refers to any natural or legal person who processes data on the Data Controller's behalf. We may employ the services of a variety of Service Providers to more efficiently process your data.
          </p>
          <p className="mb-3">
            ANY LIVING INDIVIDUAL WHO IS THE SUBJECT OF PERSONAL DATA IS THE DATA SUBJECT.
          </p>
          <p>
            THE USER is the entity that makes use of our Service. The User is the Data Subject, the individual who is the subject of Personal Data.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">3. Information collection and Utilization</h2>
          <p>
            We collect a variety of different sorts of information to provide and improve our Service.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">4. Data Types Collected</h2>
          
          <h4 className="mb-3">Individual Data</h4>
          <p className="mb-3">
            During your use of our Service, we may request that you provide us with some personally identifiable information that can be used to contact or identify you ("Personal Data"). Among the types of personally identifiable information that may be collected are, but are not limited to:
          </p>
          <ul className="mb-4">
            <li>0.1. Electronic mail address</li>
            <li>0.2. Initials and surname</li>
            <li>0.3. Contact information</li>
            <li>0.4. Country, State, Province, ZIP/Postal code, and City.</li>
            <li>0.5. Cookies and Other data Technologies</li>
          </ul>
          <p className="mb-4">
            We may contact you using your Data to send you newsletters, marketing or promotional materials, and other information that we believe will be of interest to you. You may unsubscribe from any or all of these communications by clicking on the unsubscribe link on My Account.
          </p>

          <h4 className="mb-3">Lead Information (For Real Estate Agents):</h4>
          <p className="mb-3">When using lead management features, we collect:</p>
          <ul className="mb-3">
            <li>Contact information (name, phone, email)</li>
            <li>Lead status and notes</li>
            <li>Follow-up dates</li>
            <li>Conversion history</li>
            <li>Communication preferences</li>
          </ul>
          <p className="mb-4">This lead data is treated as Personal Data and subject to all protections in this Privacy Policy.</p>

          <h4 className="mb-3">Phone Number Collection and Usage:</h4>
          <ul className="mb-3">
            <li>Phone numbers are required for Property Owners and Real Estate Agents who wish to create listings</li>
            <li>Phone numbers enable direct communication between property seekers and property providers</li>
            <li>Your phone number facilitates calls and text messages from interested parties</li>
            <li>Phone numbers are NOT displayed publicly but are used when authenticated users initiate contact through Call or Message buttons</li>
            <li>You maintain full control over your phone number and can update or remove it at any time</li>
            <li>Removing your phone number will disable the Call/Message contact features for your listings</li>
          </ul>

          <h4 className="mb-3">Usage data</h4>
          <p className="mb-3">
            Additionally, we may collect information sent by your browser whenever you visit our Service or access it via or through any device ("Usage Data").
          </p>
          <p className="mb-3">
            This Usage Data may comprise the Internet Protocol address (e.g., IP address) of your computer, the browser type and version of your browser, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
          </p>
          <p className="mb-4">
            When you use a device to access the Service, this Usage Data may contain information on the type of device you are using, its unique identifier, its IP address, its operating system, the type of Internet browser you are using, and other diagnostic data.
          </p>

          <h4 className="mb-3">Guest / Anonymous Users</h4>
          <p className="mb-3">
            If you use eTENANT without registering, you are assigned an anonymous session (guest mode). We collect:
          </p>
          <ul className="mb-3">
            <li>An anonymous user identifier (stored locally on your device)</li>
            <li>Your approximate country (detected via IP address for currency selection)</li>
            <li>Any property, tenant, or rental data you enter in the app</li>
          </ul>
          <p className="mb-4">
            Guest data is associated with your anonymous session and stored in our database for the duration of the session. If you register an account, all guest data is permanently linked to your account. If you clear app data or your session expires, guest data cannot be recovered. Guest users may be prompted to register to preserve their data.
          </p>

          <h4 className="mb-3">Location Data</h4>
          <p className="mb-3">
            If you permit us, we may collect and retain information about your location ("Location Data"). We use this data to operate our Service, improve it, and customize it.
          </p>
          <p className="mb-3">
            You can activate or disable location services while using our Service at any time by adjusting the settings on your device.
          </p>

          <h4 className="mb-3">GPS Location for Public Listings Map (Optional Feature)</h4>
          <p className="mb-3">When you use the Map view in public listings, we may request access to your device's GPS location to:</p>
          <ul className="mb-3">
            <li>Show nearby properties within your specified radius (10km default)</li>
            <li>Center the map on your current location</li>
            <li>Calculate distances to property listings</li>
            <li>Provide location-based search functionality</li>
          </ul>

          <h5 className="mb-3">What we collect:</h5>
          <ul className="mb-3">
            <li>Your current GPS coordinates (latitude/longitude) - only when you use the map feature</li>
            <li>Approximate location radius for nearby property filtering</li>
          </ul>

          <h5 className="mb-3">What we DON'T do:</h5>
          <ul className="mb-3">
            <li>We do NOT store your GPS location permanently</li>
            <li>We do NOT track your location history</li>
            <li>We do NOT share your GPS coordinates with property owners/agents</li>
            <li>We do NOT access GPS in the background</li>
            <li>Location is used only during active map sessions</li>
          </ul>

          <h5 className="mb-3">Your Control:</h5>
          <ul className="mb-3">
            <li>GPS location is completely optional - you can browse all listings without enabling location</li>
            <li>You can deny location permission and still use the map (shows all properties)</li>
            <li>Location permission can be granted/revoked anytime through device settings</li>
            <li>The map works without GPS, showing all available properties</li>
          </ul>

          <h5 className="mb-3">Privacy Protection:</h5>
          <ul className="mb-4">
            <li>GPS coordinates are processed locally on your device</li>
            <li>Only anonymous distance calculations are performed</li>
            <li>Property locations are public (for published listings), but your location remains private</li>
          </ul>

          <h4 className="mb-3">Cookie Data Monitoring</h4>
          <p className="mb-3">
            We track user behavior on our Service using cookies and similar tracking technologies and save certain information.
          </p>
          <p className="mb-3">
            Cookies are little data files that may contain an anonymous unique identifier. Cookies are little text files that are transmitted to your browser by a website and stored on your device. Additional tracking technologies, including beacons, tags, and scripts, are also used to collect and track information, as well as to develop and analyze our Service.
          </p>
          <p className="mb-3">
            You may configure your browser to refuse all cookies or to notify you when a cookie is transmitted. However, if you disable cookies, you may be unable to use certain features of our Service.
          </p>
          <p className="mb-3">Cookies that we utilize include the following:</p>
          <ul className="mb-4">
            <li>0.1. Session Cookies: To operate our Service, we employ Session Cookies.</li>
            <li>0.2. Preference Cookies: Preference Cookies are used to store information about your preferences and other settings.</li>
            <li>0.3. Security Cookies: We utilize Security Cookies to ensure your security.</li>
            <li>0.4. Analytics Cookies: Analytics Cookies help us understand how users interact with our Service to improve functionality and user experience.</li>
          </ul>

          <h4 className="mb-3">Additional Information</h4>
          <p>
            Additionally, while using our Service, we may collect the following information: age, date of birth, place of birth, passport information, citizenship, registration at the place of residence and actual address, telephone number (work, mobile), details of educational, qualification, professional training, employment agreements, NDA agreements, information on bonuses and compensation, and information on marital status and family members.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">5. Data Utilization</h2>
          <p className="mb-3">The INVOVANE COMPANY collects data for a variety of purposes:</p>
          <ul className="mb-3">
            <li>0.1. to provide and maintain our Service;</li>
            <li>0.2. to keep you informed of changes to our Service;</li>
            <li>0.3. to enable you to interact with interactive parts of our Service if you so want;</li>
            <li>0.4. to offer client service;</li>
            <li>0.5. to conduct analysis or collect valuable information to improve our Service;</li>
            <li>0.6. to track how our Service is used;</li>
            <li>0.7. to identify, avoid, and resolve technical problems;</li>
            <li>0.8. to accomplish any other purpose for which it was provided;</li>
            <li>0.9. to fulfill our duties and enforce our rights under any contracts we enter with you, including billing and collection;</li>
            <li>0.10. to send you notices regarding your account and/or subscription, such as expiration and renewal notices, as well as email instructions;</li>
            <li>0.11. to send you news, special offers, and general information about other goods, services, and events that we offer that are comparable to those you have already purchased or enquired about unless you have indicated that you do not wish to receive such information;</li>
            <li>0.12. in any other manner that we may specify when you submit the information;</li>
            <li>0.13. with your consent, for any other purpose.</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">6. Data Retention</h2>
          <p className="mb-3">
            We will retain your data for as long as necessary to fulfill the purposes outlined in this Privacy policy. We will retain and use your Data as necessary to comply with our legal duties (for example, if relevant laws compel us to store your data), resolve disputes, and enforce our legal agreements and policies.
          </p>
          <p>
            Additionally, we shall store Usage Data for internal analysis. Usage Data is normally stored for a shorter amount of time, unless when this data is utilized to increase the security or operation of our Service, or where we are legally compelled to retain this data for a longer period.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">7. Data Transfer</h2>
          <p className="mb-3">
            Your information, including Personal Data, may be transferred to – and retained on – computers located outside of your state, province, country, or other governmental jurisdiction, where data protection regulations may differ.
          </p>
          <p className="mb-3">
            Your data may be transferred to and processed in the Philippines (where INVOVANE COMPANY is registered), as well as other countries where our service providers operate (including the United States and the European Union).
          </p>
          <p className="mb-3">
            Your agreement to this Privacy Policy, followed by the submission of such information, constitutes your consent to that transfer.
          </p>
          <p className="mb-3">
            INVOVANE COMPANY will take all reasonable steps to ensure that your data is treated securely and in accordance with this Privacy Policy, and no transfer of your Personal Data to an organization or country will occur unless adequate controls are in place, including:
          </p>
          <ul>
            <li>Standard Contractual Clauses (SCCs) for transfers from the EU/EEA</li>
            <li>UK International Data Transfer Agreements (IDTAs) for transfers from the UK</li>
            <li>Compliance with applicable data protection adequacy frameworks</li>
            <li>Technical and organizational security measures</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">8. Data Disclosure</h2>
          <p className="mb-3">We may disclose personal information that we collect or that you supply to the following entities:</p>
          
          <h4 className="mb-3">0.1. Law Enforcement Disclosure.</h4>
          <p className="mb-3">
            We may be forced to disclose your Personal Data in some circumstances, such as where compelled by law or in response to authorized requests by public authorities.
          </p>

          <h4 className="mb-3">0.2. Commercial Transaction.</h4>
          <p className="mb-3">
            Your Personal Data may be transferred if we or our subsidiaries are involved in a merger, acquisition, or asset sale.
          </p>

          <h4 className="mb-3">0.3. Additional instances.</h4>
          <p className="mb-3">Additionally, we may disclose your information as follows:</p>
          <ul className="mb-3">
            <li>0.3.1. To our subsidiaries and affiliates;</li>
            <li>0.3.2. to contractors, service providers, and other third-party service providers who assist us in conducting our business, including Google Play for payment processing;</li>
            <li>0.3.3. to accomplish the purpose for which it was provided;</li>
            <li>0.3.4. in purpose to display your company's logo on our website;</li>
            <li>0.3.5. for any other purpose that we disclose to you when you supply us with the information;</li>
            <li>0.3.6. in all other instances, with your approval;</li>
            <li>0.3.7. if we believe disclosure is required or appropriate to protect the Company's, our customers', or others' rights, property, or safety.</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">9. Data Security</h2>
          <p className="mb-3">
            While we value the security of your data, keep in mind that no method of transmission over the Internet or technique of electronic storage is completely safe. While we make every effort to protect your Data in a commercially reasonable manner, we cannot guarantee its perfect security.
          </p>

          <h4 className="mb-3">Multi-Client Data Separation (For Real Estate Agents):</h4>
          <ul className="mb-3">
            <li>All data for different properties and clients is logically separated within your account</li>
            <li>You control access to this data and are responsible for maintaining client confidentiality</li>
            <li>eTENANT implements technical measures to ensure data separation between different clients' records</li>
            <li>You remain the data controller for client information entered into the system</li>
            <li>Access controls prevent accidental mixing of data between different properties or clients</li>
          </ul>

          <h4 className="mb-3">Data Breach Notification:</h4>
          <p className="mb-3">In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will:</p>
          <ul className="mb-3">
            <li>Notify the relevant supervisory authority within 72 hours of becoming aware of the breach (as required by GDPR, UK GDPR, Australian Notifiable Data Breaches scheme, Singapore PDPA, and other applicable laws)</li>
            <li>Notify affected users without undue delay if the breach is likely to result in a high risk to their rights and freedoms</li>
            <li>Provide details of: the nature of the breach, categories of data affected, likely consequences, and measures taken or proposed to address the breach</li>
            <li>Maintain an internal breach register documenting all data security incidents</li>
            <li>Cooperate with relevant data protection authorities during any investigation</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">10. Your Rights Regarding Data Protection In accordance with the General Data Protection Regulation (GDPR)</h2>
          <p className="mb-3">
            If you live in the European Union (EU), the European Economic Area (EEA), or the United Kingdom (UK), you have certain data protection rights under the GDPR (and UK GDPR). See also Section 12A for UK-specific provisions.
          </p>
          <p className="mb-3">
            We will make commercially reasonable efforts to ensure that your Personal Data is accurate, complete, and up to date.
          </p>
          <p className="mb-3">
            If you would like to know what Personal Data we possess on you and request that it be deleted from our systems, please contact us via email at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>.
          </p>
          <p className="mb-3">You have the following data protection rights in certain circumstances:</p>
          <ul className="mb-3">
            <li>0.1. the right to request access to, update, or deletion of the information we maintain about you;</li>
            <li>0.2. the rectification right. You have the right to have erroneous or incomplete information about you updated;</li>
            <li>0.3. the right to object. You have the right to object to the way your Personal Data is used by us;</li>
            <li>0.4. the limit right. You have the right to request a restriction on how your personal information is processed;</li>
            <li>0.5. the right to portability of data. You have the right to obtain a copy of your Personal Data in a structured, machine-readable, and frequently used format.</li>
            <li>0.6. the right to revoke consent. Additionally, you have the right to withdraw your consent at any time for us to handle your personal information based on your consent;</li>
          </ul>
          <p className="mb-3">
            Please be aware that we may require verification of your identity before responding to such inquiries. Please keep in mind that we may be unable to provide the Service if certain data is not provided.
          </p>
          <p>
            You have the right to lodge a collection with a Data Protection Authority regarding how we collect and use your Personal Data. For additional information, please contact your European Economic Area's data protection authorities (EEA).
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">11. Your Data Protection Rights under the California Privacy Protection Act (CalOPPA)</h2>
          <p className="mb-3">
            CalOPPA is the nation's first state law requiring commercial websites and online services to disclose a privacy policy. The law's reach extends well beyond California, requiring any person or business in the United States (and conceivably the world) that operates websites that collect personally identifiable information from California residents to prominently display a privacy policy on their website outlining the information being collected and the individuals with whom it will be shared, as well as to adhere to this statement.
          </p>
          <p className="mb-3">CalOPPA implies that we agree with the following:</p>
          <ul className="mb-3">
            <li>0.1. users can visit our site anonymously;</li>
            <li>0.2. our Privacy Policy link includes the word "Privacy" and is immediately accessible from our web site's home page;</li>
            <li>0.3. Any modifications to our privacy policies will be communicated to users via our Privacy Policy Page;</li>
            <li>0.4. Users may update their personal information at any time by emailing us through email at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>.</li>
          </ul>
          <p className="mb-3">Our Policy Regarding "Do Not Track" (DNT) Signals:</p>
          <p className="mb-3">
            When a "Do Not Track" browser feature is enabled, we respect Do Not Track signals and do not track plant cookies, or employ advertising. Do Not Track is a web browser feature that you can enable to alert websites that you do not wish to be monitored.
          </p>
          <p>
            You can enable or disable Do Not Track by accessing your web browser's Preferences or Settings page.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12. California Consumer Privacy Act (CCPA): Your Data Protection Rights</h2>
          <p className="mb-3">
            If you live in California, you have the right to request information about the data we collect about you, to have it deleted, and to have it not sold (shared). To exercise your data protection rights, you may submit certain requests and inquire about the following:
          </p>
          
          <h4 className="mb-3">0.1. What personally identifiable information we hold about you.</h4>
          <p className="mb-3">If you submit this request, we will respond as follows:</p>
          <ul className="mb-3">
            <li>0.0.1. The types of personal information about you that we have gathered.</li>
            <li>0.0.2. The sources from which we collect your personally identifiable information.</li>
            <li>0.0.3. The business or commercial purpose for which your personal information is collected or sold.</li>
            <li>0.0.4. The types of third parties with whom we disclose personally identifiable information.</li>
            <li>0.0.5. The exact pieces of personal information about you that we have gathered.</li>
            <li>0.0.6. A list of the categories of personal information we have sold, as well as the category of the company to which we sold it. If we do not sell your personal information, we will notify you.</li>
            <li>0.0.7. A list of the categories of personal information to which we have disclosed it for commercial purposes, as well as the category of any other firm with which we shared it.</li>
          </ul>
          <p className="mb-3">
            Please note that you have the right to request this information from us up to two times per rolling twelve-month period. When you submit your request, the information you receive may be limited to the personal information we have gathered about you in the preceding 12 months.
          </p>
          
          <h4 className="mb-3">0.2. To ensure that your personal information is deleted.</h4>
          <p className="mb-3">
            If you make this request, we will erase all personal information about you that we hold as of the date of your request from our records and will instruct any service providers to do the same. In some instances, deletion may be performed by de-identifying the data. If you opt to erase your personal information, you may lose information to some functions that rely on it.
          </p>
          
          <h4 className="mb-3">0.3. To discontinue the sale of your personal information.</h4>
          <p className="mb-3">
            We do not sell or rent your personally identifiable information to third parties for any purpose. We will never sell your personal information for financial gain. However, in certain situations, a transfer of personal information without consideration to a third party or within our family of organizations may be considered a "sale" under California law. You are the sole owner of your Data and may at any time request its disclosure or erasure.
          </p>
          <p className="mb-3">
            We will cease selling your personal information if you submit a request to do so.
          </p>
          <p className="mb-3">
            Please keep in mind that if you request that we delete or stop selling your data, this may impair your experience with us and may prevent you from participating in certain programs or membership services that need the use of your personal information to function. However, we will never discriminate against you for exercising your rights.
          </p>
          <p className="mb-3">
            To exercise your California data protection rights as outlined above, please send an email to <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> with your request(s).
          </p>
          <p>
            The CCPA, abbreviated for the California Consumer Privacy Act, protects the data protection rights outlined above. To learn more, visit the official website of the California Legislative Information. The CCPA became effective on January 1, 2020.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12A. United Kingdom Data Protection Rights</h2>
          <p className="mb-3">
            If you reside in the United Kingdom, you have rights under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. These rights mirror GDPR rights outlined in Section 10, with the following UK-specific provisions:
          </p>
          <ul className="mb-3">
            <li>Supervisory Authority: You may lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk</li>
            <li>International Transfers: Where your data is transferred outside the UK, we ensure appropriate safeguards are in place, including Standard Contractual Clauses or UK International Data Transfer Agreements (IDTAs)</li>
            <li>All references to "EU/EEA" in this Privacy Policy also apply to the United Kingdom</li>
            <li>You have a 14-day cooling-off period for subscription purchases under the Consumer Contracts Regulations 2013</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12B. Australian Privacy Rights</h2>
          <p className="mb-3">
            If you reside in Australia, the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs) apply to your personal information. In addition to the rights described above:
          </p>
          <ul className="mb-3">
            <li>You have the right to access your personal information and request correction of inaccurate data (APP 12 and APP 13)</li>
            <li>We will only collect personal information that is reasonably necessary for our functions (APP 3)</li>
            <li>You may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au</li>
            <li>Under the Notifiable Data Breaches (NDB) scheme, we will notify you and the OAIC if a data breach is likely to result in serious harm</li>
            <li>We will not disclose your personal information to overseas recipients without ensuring they comply with the APPs or you consent to the disclosure</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12C. Canadian Privacy Rights</h2>
          <p className="mb-3">
            If you reside in Canada, the Personal Information Protection and Electronic Documents Act (PIPEDA) and, for Quebec residents, the Act Respecting the Protection of Personal Information in the Private Sector (Quebec Law 25) apply. You have the following rights:
          </p>
          <ul className="mb-3">
            <li>Access to your personal information held by us</li>
            <li>Correction of inaccurate or incomplete personal information</li>
            <li>Withdrawal of consent to the collection, use, or disclosure of your personal information</li>
            <li>You may lodge a complaint with the Office of the Privacy Commissioner of Canada (OPC) at priv.gc.ca</li>
            <li>Quebec residents may contact the Commission d'acc&egrave;s &agrave; l'information du Qu&eacute;bec (CAI)</li>
            <li>We adhere to the 10 fair information principles under PIPEDA</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12D. Singapore Privacy Rights (PDPA)</h2>
          <p className="mb-3">
            If you reside in Singapore, the Personal Data Protection Act 2012 (PDPA) applies. You have the following rights:
          </p>
          <ul className="mb-3">
            <li>Access to your personal data and information about how it has been used or disclosed in the past year</li>
            <li>Correction of inaccurate personal data</li>
            <li>Withdrawal of consent (subject to legal or contractual restrictions)</li>
            <li>Data portability rights under the Data Portability Obligation</li>
            <li>You may lodge a complaint with the Personal Data Protection Commission (PDPC) at pdpc.gov.sg</li>
            <li>We will notify the PDPC within 72 hours of any notifiable data breach</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12E. United Arab Emirates Privacy Rights (PDPL)</h2>
          <p className="mb-3">
            If you reside in the United Arab Emirates, the Personal Data Protection Law (Federal Decree-Law No. 45 of 2021) applies:
          </p>
          <ul className="mb-3">
            <li>Your consent for data processing is obtained separately from these Terms</li>
            <li>You have the right to access, correct, and delete your personal data</li>
            <li>You may request restriction of processing or data portability</li>
            <li>You may lodge a complaint with the UAE Data Office</li>
            <li>Cross-border data transfers comply with UAE PDPL requirements</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12F. South African Privacy Rights (POPIA)</h2>
          <p className="mb-3">
            If you reside in South Africa, the Protection of Personal Information Act (POPIA) applies:
          </p>
          <ul className="mb-3">
            <li>You have the right to access, correct, and delete your personal information</li>
            <li>You may object to the processing of your personal information</li>
            <li>You have the right not to be subject to automated decision-making</li>
            <li>You may lodge a complaint with the Information Regulator at inforegulator.org.za</li>
            <li>We process your data in accordance with the 8 conditions for lawful processing under POPIA</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12G. Nigerian Privacy Rights (NDPA)</h2>
          <p className="mb-3">
            If you reside in Nigeria, the Nigeria Data Protection Act 2023 (NDPA) applies:
          </p>
          <ul className="mb-3">
            <li>You have the right to access, correct, and delete your personal data</li>
            <li>You may object to data processing and withdraw consent</li>
            <li>You have data portability rights</li>
            <li>You may lodge a complaint with the Nigeria Data Protection Commission (NDPC)</li>
            <li>We comply with the data protection principles set out in the NDPA</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12H. Indian Privacy Rights (DPDP Act)</h2>
          <p className="mb-3">
            If you reside in India, the Digital Personal Data Protection Act 2023 (DPDP Act) applies:
          </p>
          <ul className="mb-3">
            <li>You have the right to access, correct, and erase your personal data</li>
            <li>You have the right to nominate a person to exercise your rights in case of death or incapacity</li>
            <li>You may lodge a grievance with our Grievance Officer at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a></li>
            <li>You may approach the Data Protection Board of India for complaints</li>
            <li>We obtain your consent before processing personal data and provide clear notice of the purpose of processing</li>
            <li>Users must be at least 18 years old; we do not knowingly process data of children without verifiable parental consent</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">12I. New Zealand Privacy Rights</h2>
          <p className="mb-3">
            If you reside in New Zealand, the Privacy Act 2020 applies:
          </p>
          <ul className="mb-3">
            <li>You have the right to access and correct your personal information (Information Privacy Principles 6 and 7)</li>
            <li>We will only collect, use, and disclose personal information in accordance with the Information Privacy Principles</li>
            <li>You may lodge a complaint with the Office of the Privacy Commissioner at privacy.org.nz</li>
            <li>We will notify the Privacy Commissioner and affected individuals of notifiable privacy breaches</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">13. Phone Contacts Access</h2>
          <p className="mb-3"><strong>This feature is optional and user-initiated.</strong></p>
          <p className="mb-3">
            eTENANT may request permission to access your device's phone contacts to simplify the process of adding contact information (tenants, property owners, leads, etc.).
          </p>

          <h4 className="mb-3">What we collect from your contacts:</h4>
          <ul className="mb-3">
            <li>Contact names (first and last name)</li>
            <li>Phone numbers</li>
            <li>Email addresses</li>
          </ul>

          <h4 className="mb-3">How we use contacts data:</h4>
          <ul className="mb-3">
            <li>To auto-fill contact information forms when you choose to import a contact</li>
            <li>To simplify data entry and reduce errors when creating records</li>
            <li>Contacts are accessed only when you explicitly choose to import a contact</li>
          </ul>

          <h4 className="mb-3">What we DON'T do with your contacts:</h4>
          <ul className="mb-3">
            <li>We do NOT automatically upload or store your entire contacts list</li>
            <li>We do NOT share, sell, or transfer contact information to third parties</li>
            <li>We do NOT use contacts for marketing or advertising purposes</li>
            <li>We do NOT access contacts without your explicit permission</li>
          </ul>

          <h4 className="mb-3">Your Rights and Control:</h4>
          <ul className="mb-3">
            <li>Contacts permission is completely optional - you can add information manually</li>
            <li>You choose which specific contact to import each time</li>
            <li>Only the selected contact's information is used to pre-fill the form</li>
            <li>You can revoke contacts permission at any time through your device settings</li>
            <li>Revoking permission does not affect existing records</li>
          </ul>

          <h4 className="mb-3">Data Retention:</h4>
          <ul className="mb-3">
            <li>Contact information imported becomes part of the record you create</li>
            <li>This data is stored as regular information in our database</li>
            <li>No separate copy of your contacts list is maintained</li>
            <li>All records follow our standard data retention policies</li>
          </ul>

          <h4 className="mb-3">Compliance:</h4>
          <ul className="mb-3">
            <li>We request contacts permission following Google Play's User Data policy</li>
            <li>A prominent disclosure is shown before requesting permission</li>
            <li>Permission is requested only when you attempt to use the import feature</li>
            <li>You maintain full control over granting or denying this permission</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">14. Data Storage and File Handling</h2>
          <p className="mb-3">eTENANT uses multiple storage solutions to protect your data while enabling service functionality.</p>

          <h3 className="mb-4">A. Property Image Storage via Supabase (For Property Owners and Real Estate Agents)</h3>
          <p className="mb-3">eTENANT stores property images you upload on Supabase Cloud Storage, a secure third-party infrastructure provider.</p>

          <h4 className="mb-3">User Eligibility:</h4>
          <ul className="mb-3">
            <li>Only registered Property Owners and Real Estate Agents may upload property images</li>
            <li>Public users, tenants, and guests cannot upload images (view-only access)</li>
            <li>Paid subscription required for image uploads</li>
            <li>Identity verification may be required for bulk uploads</li>
          </ul>

          <h4 className="mb-3">What we collect when you upload images:</h4>
          <ul className="mb-3">
            <li>Image files (JPEG, PNG, WebP formats up to 5MB each)</li>
            <li>Original file names (for your reference only)</li>
            <li>Upload timestamps (UTC timezone)</li>
            <li>File sizes in bytes</li>
            <li>Image dimensions (width x height in pixels)</li>
            <li>MIME types (image/jpeg, image/png, etc.)</li>
            <li>User ID (to associate images with your account)</li>
            <li>Property ID (to link images to specific properties)</li>
          </ul>

          <h4 className="mb-3">What we AUTOMATICALLY REMOVE from images (Privacy Protection):</h4>
          <ul className="mb-3">
            <li>EXIF metadata (complete removal for privacy)</li>
            <li>GPS location coordinates (latitude/longitude)</li>
            <li>Camera make, model, and settings</li>
            <li>Original creation date/time from camera</li>
            <li>Photographer copyright information from metadata</li>
            <li>Software used to edit images (Photoshop, Lightroom, etc.)</li>
            <li>Camera serial numbers</li>
            <li>Any other embedded metadata</li>
          </ul>

          <h4 className="mb-3">Why we strip EXIF data:</h4>
          <ul className="mb-3">
            <li>Protects your privacy (prevents GPS tracking)</li>
            <li>Protects photographer privacy</li>
            <li>Reduces file sizes</li>
            <li>Prevents unintended disclosure of equipment information</li>
            <li>Complies with GDPR "data minimization" principle</li>
          </ul>

          <h4 className="mb-3">What you should still review manually:</h4>
          <ul className="mb-3">
            <li>Visible personal information in images (documents, IDs, mail)</li>
            <li>Identifiable people in photos (faces, uniforms, badges)</li>
            <li>License plates or vehicle identification numbers</li>
            <li>Property addresses or unit numbers (if you want privacy)</li>
            <li>Tenant belongings or personal items</li>
            <li>Reflections in mirrors or windows showing people</li>
            <li>Computer screens or device displays with private information</li>
          </ul>

          <h4 className="mb-3">How images are stored:</h4>
          <ul className="mb-3">
            <li>Uploaded via secure HTTPS connections (TLS 1.3 encryption)</li>
            <li>Stored in Supabase private storage buckets with access controls</li>
            <li>Organized by path: user_id/property_id/image_id.extension</li>
            <li>Encrypted at rest by Supabase infrastructure (AES-256)</li>
            <li>Stored in Supabase data centers (primary location: AWS infrastructure with global distribution)</li>
          </ul>

          <h4 className="mb-3">Image Access Control:</h4>
          <ul className="mb-3">
            <li>Your uploaded images are private to your account</li>
            <li>Only you (and, where applicable, agents or owners with access to the associated property) can view your property images</li>
            <li>Deleting images permanently removes them from storage</li>
          </ul>

          <h4 className="mb-3">Your Rights Regarding Images:</h4>
          <ul className="mb-3">
            <li>Access: View all your uploaded images through the app</li>
            <li>Download: Not currently supported (images remain in cloud storage)</li>
            <li>Delete: Remove images at any time (deletion is permanent and immediate)</li>
            <li>Modify: Replace images or change main image designation</li>
            <li>Correct: Replace incorrect or outdated images</li>
            <li>Portability: Request bulk image export (contact <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>)</li>
          </ul>

          <h4 className="mb-3">Image Retention and Deletion:</h4>
          <ul className="mb-3">
            <li>Active images: Retained while your account is active</li>
            <li>Deleted images: Permanently removed from storage within 24-48 hours (cannot be recovered)</li>
            <li>Account deletion: All images permanently deleted within 30 days</li>
            <li>Metadata records: Image references retained for 10 years per applicable data protection law requirements (actual image files deleted per schedules above)</li>
            <li>Takedown requests: Images removed within 48-72 hours and permanently deleted</li>
            <li>Backup retention: Deleted images may remain in backups for up to 30 days, then permanently purged</li>
          </ul>

          <h4 className="mb-3">Third-Party Service Provider:</h4>
          <ul className="mb-3">
            <li>Supabase Inc. (infrastructure provider)
              <ul>
                <li>Website: https://supabase.com</li>
                <li>Privacy Policy: https://supabase.com/privacy</li>
                <li>Location: Global (AWS infrastructure)</li>
                <li>Compliance: GDPR, SOC 2 Type II, ISO 27001 certified</li>
                <li>Role: Infrastructure and storage provider only</li>
                <li>Access: Infrastructure-level access for storage operations</li>
                <li>Use: Does not view, analyze, or use your images for any purpose</li>
                <li>Sharing: Does not share images with third parties</li>
              </ul>
            </li>
          </ul>
          <p className="mb-3">Your images are NOT shared with any other third parties without your explicit consent</p>

          <h4 className="mb-3">Data Transfer:</h4>
          <ul className="mb-3">
            <li>Images may be stored on servers outside the Philippines (AWS global infrastructure)</li>
            <li>Transfer is secured via TLS encryption</li>
            <li>Supabase complies with GDPR adequacy requirements for international transfers</li>
            <li>You consent to international transfer when uploading images</li>
          </ul>

          <h3 className="mb-4 mt-5">B. Document Storage via Google Drive (For Paid Subscription Users)</h3>
          <p className="mb-3"><strong>This feature is available for paid subscribers only.</strong></p>
          <p className="mb-3">eTENANT does not store your uploaded documents on our servers. Instead, documents are stored in your personal Google Drive account.</p>

          <h4 className="mb-3">What we store:</h4>
          <ul className="mb-3">
            <li>References to your Google Drive files (file IDs only)</li>
            <li>Document metadata (file names, types, upload dates)</li>
            <li>Document categories (lease agreement, tenant ID, etc.)</li>
          </ul>

          <h4 className="mb-3">What we DON'T store:</h4>
          <ul className="mb-3">
            <li>Actual document files or contents</li>
            <li>Copies of your documents</li>
            <li>Backups of your documents</li>
          </ul>

          <h4 className="mb-3">Google Drive Integration:</h4>
          <ul className="mb-3">
            <li>You must authorize eTENANT to access your Google Drive</li>
            <li>Documents are stored in a dedicated "eTENANT_RentalAgreements" folder</li>
            <li>You control document retention and deletion through Google Drive</li>
            <li>Google's Privacy Policy applies to stored documents</li>
            <li>eTENANT can only access files it creates (app-scoped access)</li>
            <li>You can revoke eTENANT's access at any time via Google Account settings</li>
          </ul>

          <h4 className="mb-3">Your Rights:</h4>
          <ul className="mb-3">
            <li>Access: View all documents via eTENANT or Google Drive directly</li>
            <li>Download: Download documents via eTENANT or Google Drive</li>
            <li>Delete: Remove documents via eTENANT (deletes from your Drive)</li>
            <li>Revoke Access: Disconnect eTENANT from Google Drive anytime</li>
          </ul>

          <h4 className="mb-3">Data Retention:</h4>
          <ul className="mb-3">
            <li>eTENANT retains document metadata for 10 years (as required by applicable data protection laws)</li>
            <li>Actual documents remain in your Google Drive indefinitely (unless you delete them)</li>
            <li>Deleting documents from eTENANT also deletes them from your Google Drive</li>
          </ul>

          <h4 className="mb-3">Compliance:</h4>
          <ul className="mb-3">
            <li>Google Drive is GDPR compliant, SOC 2 certified, ISO 27001 certified</li>
            <li>Google complies with international data protection regulations</li>
            <li>Your documents are encrypted at rest and in transit by Google</li>
            <li>You choose your Google Drive storage region (data residency)</li>
          </ul>

          <h3 className="mb-4 mt-5">C. Database Storage via Supabase</h3>
          <ul className="mb-3">
            <li>Property information, user profiles, rental agreements, payments, and other structured data stored in Supabase PostgreSQL databases</li>
            <li>Data encrypted at rest and in transit</li>
            <li>Regular automated backups</li>
            <li>Retention: 10 years for business records, 30 days for personal data after account deletion</li>
            <li>See Section 6 "Data Retention" for detailed retention policies</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">15. Service Providers</h2>
          <p className="mb-3">
            We may hire third-party companies and individuals to facilitate our Service ("Service Providers"), to offer Service on our behalf, to conduct Service-related functions, or to assist us in analyzing how our Service is utilized.
          </p>
          <p className="mb-3">Our primary service providers include:</p>
          <ul className="mb-3">
            <li>Google LLC: For document storage (Google Drive), payment processing (Google Play), authentication, and analytics</li>
            <li>Google AdMob (Google LLC): For displaying ads to free plan users</li>
            <li>Supabase Inc.: For database and backend services</li>
            <li>Mailtrap (Railsware Products, Inc.): For transactional email delivery</li>
            <li>Firebase (Google LLC): For push notifications</li>
          </ul>

          <h4 className="mb-3">Third-Party Service Provider Details:</h4>
          <ul className="mb-3">
            <li>Google LLC - Authentication (Google Sign-In)
              <ul>
                <li>Used for: User account authentication and login</li>
                <li>Data collected from Google: Name, email address, and profile photo URL</li>
                <li>These are used solely to create and identify your eTENANT account</li>
                <li>We do not access any other Google account data beyond basic profile information</li>
                <li>Subject to Google's Privacy Policy: https://policies.google.com/privacy</li>
              </ul>
            </li>
            <li>Google LLC - Document storage and management
              <ul>
                <li>Subject to Google's Privacy Policy: https://policies.google.com/privacy</li>
                <li>Google Drive Terms of Service: https://www.google.com/drive/terms-of-service/</li>
              </ul>
            </li>
            <li>Google Play - Payment processing and subscription management
              <ul>
                <li>Handles payment transactions according to PCI DSS compliance standards</li>
              </ul>
            </li>
            <li>Supabase Inc. - Database services
              <ul>
                <li>Stores document metadata only (NOT actual files)</li>
                <li>Data Processing Addendum available</li>
              </ul>
            </li>
            <li>Mailtrap (Railsware Products, Inc.) - Transactional email delivery
              <ul>
                <li>Used for: OTP verification codes, rent and recurring charge due reminders, payment statements</li>
                <li>Data processed: Email addresses, email content (OTP codes, rental information)</li>
                <li>Privacy Policy: https://mailtrap.io/privacy-policy</li>
                <li>Data retention: Emails retained per Mailtrap's data processing terms</li>
                <li>Location: EU/US infrastructure</li>
              </ul>
            </li>
            <li>Firebase Cloud Messaging (Google LLC) - Push notifications
              <ul>
                <li>Used for: Rent and recurring charge due reminders, subscription notifications, system alerts</li>
                <li>Data processed: FCM device tokens, notification content</li>
                <li>Privacy Policy: https://firebase.google.com/support/privacy</li>
              </ul>
            </li>
          </ul>
          <p>
            These third parties have access to your Personal Data only to carry out these duties on our behalf and are bound to keep it confidential and not to disclose or use it for any other purpose.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">16. Public Listings Data Flow (For All Users)</h2>
          <p className="mb-3">eTENANT provides a public listings feature that allows anyone to browse published property listings without creating an account.</p>

          <h3 className="mb-4">A. Who Can Access Public Listings</h3>
          <p className="mb-3">Anyone with internet access:</p>
          <ul className="mb-3">
            <li>Tenants searching for rental properties</li>
            <li>Property buyers looking for properties for sale</li>
            <li>General public interested in real estate</li>
            <li>Search engine crawlers (Google, Bing, DuckDuckGo, etc.)</li>
            <li>Social media platforms (when links are shared)</li>
            <li>Real estate aggregators (if we integrate in future)</li>
            <li>No login or account creation required to browse</li>
          </ul>

          <h3 className="mb-4">B. What Information is Publicly Visible</h3>
          <p className="mb-3">When browsing public listings, anyone can see:</p>
          <ul className="mb-3">
            <li>Property images uploaded by owners/agents</li>
            <li>Property address (full street address, city, region)</li>
            <li>Property type (house, apartment, condo, commercial, etc.)</li>
            <li>Property features:
              <ul>
                <li>Number of bedrooms</li>
                <li>Number of bathrooms</li>
                <li>Number of garages/parking spaces</li>
                <li>Floor area (square feet/meters)</li>
                <li>Maximum occupancy (pax)</li>
                <li>Amenities and features</li>
              </ul>
            </li>
            <li>Listing description (written by owner/agent)</li>
            <li>Pricing information:
              <ul>
                <li>Daily or monthly rent (for rental properties)</li>
                <li>Sale price (for properties for sale)</li>
              </ul>
            </li>
            <li>Property location on map (if enabled)</li>
            <li>Listing publish date and last updated date</li>
            <li>Property images gallery with thumbnail navigation</li>
          </ul>

          <h3 className="mb-4">C. What Information is NOT Publicly Visible</h3>
          <p className="mb-3">Your privacy is protected - the following is hidden from public view:</p>
          <ul className="mb-3">
            <li>Property owner or agent personal contact information:
              <ul>
                <li>Email addresses</li>
                <li>Phone numbers (NOT displayed publicly on listing pages, but accessible to authenticated users who click Call/Message buttons to initiate contact)</li>
                <li>Mobile numbers</li>
                <li>Personal addresses</li>
              </ul>
            </li>
            <li>Property owner or agent full names (listed as "Property Owner" or "Real Estate Agent")</li>
            <li>User account details and credentials</li>
            <li>Payment or financial transaction information</li>
            <li>Private notes, comments, or internal property data</li>
            <li>Rental agreement terms and tenant information</li>
            <li>Maintenance requests or property management history</li>
            <li>Communication preferences or messaging history</li>
            <li>Other properties you manage (unless also published separately)</li>
            <li>Your subscription status or payment history</li>
          </ul>

          <h3 className="mb-4">D. Data Collected from Public Viewers (Non-Authenticated Users)</h3>
          <p className="mb-3">When you browse public listings without logging in, we collect:</p>

          <h4 className="mb-3">Required for Service Operation:</h4>
          <ul className="mb-3">
            <li>IP address (for security, fraud prevention, and general location analytics)</li>
            <li>Device type (mobile, tablet, desktop)</li>
            <li>Browser type and version (Chrome, Safari, Firefox, Edge, etc.)</li>
            <li>Operating system (iOS, Android, Windows, macOS, etc.)</li>
            <li>Screen resolution and viewport size (for responsive design)</li>
            <li>Referring website (how you found our listings - Google, direct link, social media, etc.)</li>
          </ul>

          <h4 className="mb-3">Usage Analytics (Anonymous):</h4>
          <ul className="mb-3">
            <li>Pages viewed (which listings you looked at)</li>
            <li>Time spent on each listing</li>
            <li>Search queries and filter selections</li>
            <li>Date and time of visit</li>
            <li>Number of images viewed per listing</li>
            <li>Click patterns and navigation flow</li>
          </ul>

          <h4 className="mb-3">We do NOT collect from public viewers:</h4>
          <ul className="mb-3">
            <li>Names or email addresses (until you create an account)</li>
            <li>Payment information (no purchases on public listings)</li>
            <li>Precise location or GPS coordinates</li>
            <li>Microphone or camera access</li>
            <li>Contacts or address book</li>
            <li>Photos or media library</li>
            <li>Any personal information until you voluntarily create an account</li>
          </ul>

          <h3 className="mb-4">E. Cookies and Tracking Technologies</h3>
          <p className="mb-3">We use cookies for public listing functionality:</p>

          <h4 className="mb-3">Session cookies (expire when you close browser):</h4>
          <ul className="mb-3">
            <li>Remember your browsing session</li>
            <li>Track navigation within public listings</li>
            <li>Maintain search filters during your visit</li>
          </ul>

          <h4 className="mb-3">Persistent cookies (expire after set period):</h4>
          <ul className="mb-3">
            <li>Remember your language preference (future feature)</li>
            <li>Remember recently viewed listings (max 30 days)</li>
            <li>Analytics and usage statistics (Google Analytics or similar)</li>
          </ul>

          <h4 className="mb-3">Third-party cookies may be set by:</h4>
          <ul className="mb-3">
            <li>Google Analytics (if enabled) - for visitor statistics</li>
            <li>CDN providers (cached images and assets)</li>
          </ul>

          <h4 className="mb-3">You can control cookies:</h4>
          <ul className="mb-3">
            <li>Block all cookies (may break functionality)</li>
            <li>Block third-party cookies only (recommended)</li>
            <li>Clear cookies after each session</li>
            <li>Use browser privacy/incognito mode</li>
          </ul>
          <p className="mb-3">See Section 4 "Cookie Data Monitoring" for detailed cookie policy.</p>

          <h3 className="mb-4">F. Contact Initiation Process</h3>
          <p className="mb-3">To contact property owners or agents:</p>
          <ul className="mb-3">
            <li>You must create an eTENANT account</li>
            <li>You must login with your account credentials</li>
            <li>You provide: Name, email address, and inquiry message</li>
            <li>We share this information with the property owner/agent</li>
            <li>Owner/agent decides whether to respond</li>
            <li>We may collect communication history for quality assurance</li>
          </ul>

          <h3 className="mb-4">G. Property Owner/Agent Contact Information Sharing</h3>
          <p className="mb-3">When you create a listing as a Property Owner or Real Estate Agent, you consent to:</p>
          <ul className="mb-3">
            <li>Your phone number being used to facilitate direct contact with interested parties</li>
            <li>Authenticated users (those who create accounts and log in) being able to call or send SMS messages to your phone number</li>
            <li>Your phone number being shared with users who express interest in your listings through the Call or Message buttons</li>
          </ul>

          <h4 className="mb-3">How Contact Works:</h4>
          <ul className="mb-3">
            <li>Your phone number is NOT displayed publicly on listing pages</li>
            <li>Only authenticated users who click "Call" or "Message" buttons can initiate contact</li>
            <li>Clicking "Call" opens the user's phone dialer with your number</li>
            <li>Clicking "Message" opens the user's SMS app with your number</li>
            <li>You control whether to answer calls or respond to messages</li>
            <li>You may update or remove your phone number at any time through Edit Profile</li>
          </ul>

          <h4 className="mb-3">Your Rights:</h4>
          <ul className="mb-3">
            <li>You can update your phone number anytime</li>
            <li>Removing your phone number will prevent users from contacting you via Call/Message buttons</li>
            <li>You remain in full control of your contact information</li>
            <li>Contact attempts are logged for quality assurance and fraud prevention</li>
          </ul>

          <h4 className="mb-3">Data Protection:</h4>
          <ul className="mb-3">
            <li>Phone numbers are validated and sanitized for security</li>
            <li>Phone numbers are stored encrypted in our database</li>
            <li>We do not sell or share your phone number with third parties for marketing</li>
            <li>Phone numbers are retained according to our standard data retention policies (10 years for business records)</li>
          </ul>

          <h3 className="mb-4">H. Search Engine Indexing</h3>
          <p className="mb-3">Public listings are designed to be indexed by search engines:</p>
          <ul className="mb-3">
            <li>Listings may appear in Google, Bing, or other search engines</li>
            <li>Images may appear in Google Images or similar</li>
            <li>Property addresses and descriptions are searchable</li>
            <li>Search engines independently cache or archive listings</li>
            <li>We submit sitemaps to major search engines</li>
            <li>Property owners/agents consent to this indexing when publishing listings</li>
          </ul>

          <h4 className="mb-3">You cannot control:</h4>
          <ul className="mb-3">
            <li>How search engines display your listings</li>
            <li>When search engines update cached content</li>
            <li>Whether old listings remain in search results after unpublishing</li>
          </ul>

          <h3 className="mb-4">I. Social Media Sharing</h3>
          <p className="mb-3">Public listing URLs may be shared:</p>
          <ul className="mb-3">
            <li>On social media (Facebook, Twitter/X, LinkedIn, Instagram, etc.)</li>
            <li>Via messaging apps (WhatsApp, Viber, Messenger, Telegram, etc.)</li>
            <li>In emails or text messages</li>
            <li>On real estate forums or websites</li>
            <li>In group chats or community discussions</li>
          </ul>

          <h4 className="mb-3">When shared:</h4>
          <ul className="mb-3">
            <li>Open Graph meta tags show preview image, title, and description</li>
            <li>Social media platforms may cache images and content</li>
            <li>Shared links remain active while listing is published</li>
            <li>You cannot control further sharing or screenshots by others</li>
          </ul>

          <h3 className="mb-4">J. Data Processing Legal Basis</h3>
          <p className="mb-3">Our legal basis for processing public listing data:</p>

          <h4 className="mb-3">For EU/EEA users (GDPR):</h4>
          <ul className="mb-3">
            <li>Legitimate interests: Operating a property marketplace benefits property owners and property seekers</li>
            <li>Consent: Property owners consent to publication when publishing listings</li>
            <li>Contract: Providing service functionality per Terms of Service</li>
          </ul>

          <h4 className="mb-3">For California users (CCPA):</h4>
          <ul className="mb-3">
            <li>Business purpose: Facilitating property listings and search functionality</li>
            <li>Service provision: Core functionality of the application</li>
          </ul>

          <h4 className="mb-3">For Philippine users (Data Privacy Act):</h4>
          <ul className="mb-3">
            <li>Consent: Property owners consent through explicit publish action</li>
            <li>Legitimate interest: Property marketplace operations</li>
            <li>Contractual necessity: Providing agreed-upon services</li>
          </ul>

          <h4 className="mb-3">For public viewers:</h4>
          <ul className="mb-3">
            <li>Legitimate interest: Providing free property search service to the public</li>
            <li>Minimal data collection (IP address, analytics) necessary for security and service improvement</li>
          </ul>

          <h3 className="mb-4">K. Your Rights as a Public Viewer</h3>
          <p className="mb-3">Even without an account, you have rights:</p>

          <h4 className="mb-3">Right to Information:</h4>
          <ul className="mb-3">
            <li>This Privacy Policy informs you of data collection</li>
            <li>You can contact us with questions at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a></li>
          </ul>

          <h4 className="mb-3">Right to Opt-Out:</h4>
          <ul className="mb-3">
            <li>Disable cookies in your browser</li>
            <li>Use privacy-focused browsers (Firefox, Brave, etc.)</li>
            <li>Use VPN or privacy tools to mask your IP address</li>
          </ul>

          <h4 className="mb-3">Right to Object:</h4>
          <ul className="mb-3">
            <li>You can object to analytics tracking by disabling cookies</li>
            <li>You can request deletion of analytics data (email <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>)</li>
          </ul>

          <h4 className="mb-3">Right to Complain:</h4>
          <ul className="mb-3">
            <li>EU/EEA: Contact your Data Protection Authority</li>
            <li>California: File complaint with California Attorney General</li>
            <li>Philippines: File complaint with National Privacy Commission</li>
          </ul>

          <h3 className="mb-4">L. Property Owner/Agent Rights Regarding Public Listings</h3>
          <p className="mb-3">If you are a property owner or agent:</p>

          <h4 className="mb-3">Control Visibility:</h4>
          <ul className="mb-3">
            <li>Publish or unpublish listings at any time</li>
            <li>Edit listing information before publishing</li>
            <li>Delete listings permanently</li>
            <li>Archive listings (remove from public view, retain data internally)</li>
          </ul>

          <h4 className="mb-3">Modify Content:</h4>
          <ul className="mb-3">
            <li>Update property descriptions</li>
            <li>Replace or remove images</li>
            <li>Change pricing information</li>
            <li>Update property features</li>
          </ul>

          <h4 className="mb-3">Monitor Access:</h4>
          <ul className="mb-3">
            <li>View how many people viewed your listing (coming soon)</li>
            <li>See search terms that led users to your listing (coming soon)</li>
            <li>Track inquiry conversion rates (coming soon)</li>
          </ul>

          <h4 className="mb-3">Request Removal:</h4>
          <ul className="mb-3">
            <li>Property owners can request listing removal even if posted by agents</li>
            <li>Email <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> with property address and proof of ownership</li>
            <li>We will process removal within 48-72 hours</li>
          </ul>

          <h3 className="mb-4">M. Data Retention for Public Listings</h3>

          <h4 className="mb-3">Active published listings:</h4>
          <ul className="mb-3">
            <li>Visible indefinitely while published and account is active</li>
            <li>Search engine caches may persist for weeks/months after unpublishing</li>
          </ul>

          <h4 className="mb-3">Unpublished listings:</h4>
          <ul className="mb-3">
            <li>Removed from public view immediately</li>
            <li>Data retained internally for 90 days (for republishing)</li>
            <li>Automatically deleted after 90 days of inactivity</li>
          </ul>

          <h4 className="mb-3">Deleted listings:</h4>
          <ul className="mb-3">
            <li>Removed from public view immediately</li>
            <li>Permanently deleted from database within 30 days</li>
            <li>Search engine caches may persist (we cannot control this)</li>
          </ul>

          <h4 className="mb-3">Account deletion:</h4>
          <ul className="mb-3">
            <li>All listings removed from public view immediately</li>
            <li>Permanently deleted within 30 days</li>
            <li>Cannot be recovered</li>
          </ul>

          <h3 className="mb-4">N. International Data Transfers</h3>
          <p className="mb-3">Public listing data may be:</p>
          <ul className="mb-3">
            <li>Stored on servers outside the Philippines (AWS global infrastructure)</li>
            <li>Accessed by users worldwide</li>
            <li>Indexed by international search engines</li>
            <li>Cached by global CDN providers</li>
          </ul>

          <h4 className="mb-3">We ensure:</h4>
          <ul className="mb-3">
            <li>HTTPS encryption for all data transfers</li>
            <li>Compliance with GDPR adequacy requirements</li>
            <li>Supabase's international data protection certifications</li>
            <li>Your consent through use of the service</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">17. Analytics</h2>
          <p>
            Third-party Service Providers may be used to monitor and evaluate how our Service is used.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">18. Continuous integration/continuous delivery tools</h2>
          <p>
            We may utilize third-party Service Providers to automate our Service's development process.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">19. Publicity</h2>
          <p className="mb-3">
            We use Google AdMob, an advertising service provided by Google LLC, to display ads to users on the free plan. Advertising helps fund the operation and maintenance of our Service. Ads are not shown to users with an active paid subscription.
          </p>
          <p className="mb-3">
            When ads are shown, Google AdMob may collect and process:
          </p>
          <ul className="mb-3">
            <li>Your device's Advertising ID</li>
            <li>IP address and approximate location</li>
            <li>Device information (such as model, operating system, and language)</li>
            <li>Ad interaction data (such as ads viewed and clicked)</li>
          </ul>
          <p className="mb-3">
            This information is used to deliver, measure, and, where permitted, personalize ads. We do not share your properties, tenants, payments, documents, or other account records with advertisers.
          </p>
          <p className="mb-3">
            Your choices:
          </p>
          <ul className="mb-3">
            <li>You can reset or delete your Advertising ID, or opt out of personalized ads, in your device settings (Settings &gt; Google &gt; Ads, or Settings &gt; Security & privacy &gt; Privacy &gt; Ads, depending on your device).</li>
            <li>Users in the European Economic Area, the United Kingdom, and Switzerland are asked for consent before personalized ads are shown.</li>
            <li>You can remove ads entirely by upgrading to a paid subscription plan.</li>
          </ul>
          <p>
            How Google uses information from apps that use its services: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a><br />
            Google Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">20. Behavioral Remarketing</h2>
          <p>
            We may employ retargeting services to display advertisements to you on third-party websites following your visit to our Service. We and our third-party partners use cookies to collect information about your visits to our Service to improve and personalize your experience.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">21. Payments</h2>
          <p className="mb-3">
            Within the Service, we offer paid products and services through Google Play Billing. Google Play Billing is our exclusive payment processor and handles all subscription transactions. Google Play adheres to the PCI-DSS standards, which are administered by the PCI Security Standards Council, ensuring the secure management of payment information.
          </p>
          <p className="mb-3">When you make a purchase through our app:</p>
          <ul className="mb-3">
            <li>Payment data is processed directly by Google Play and is not stored on our servers</li>
            <li>Your payment information is subject to Google Play's privacy policy and security measures</li>
            <li>We receive only transaction confirmation and subscription status information from Google Play</li>
            <li>All payment disputes and refunds are handled through Google Play Store policies</li>
          </ul>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">22. Hypertext Links to Other Websites</h2>
          <p className="mb-3">
            Our Service may contain links to third-party websites that we do not control. You will be redirected to the third party's website if you click on a third party link. We strongly suggest you read the Privacy Statements of each website that you visit.
          </p>
          <p>
            We have no control over the content, privacy policies, or practices of third-party websites and disclaim any responsibility for them.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">23. Privacy of Children</h2>
          <p className="mb-3">
            Children under the age of 18 ("Child" or "Children") are not permitted to use our Services.
          </p>
          <p>
            We do not collect personally identifying information of children under the age of 18 knowingly. Please inform us if you become aware that a child has provided us with Personal Data. If we learn that we have obtained Personal Data from children without obtaining parental agreement, we immediately delete the information from our servers.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">24. Modifications to This Privacy Statement</h2>
          <p className="mb-3">
            Our Privacy policy may be updated from time to time. We will notify you of any changes to our Privacy Policy by posting the revised version on this website.
          </p>
          <p className="mb-3">
            Prior to the modification becoming effective, we will notify you through email and/or a conspicuous notice on our Service, and we will revise the "effective date" at the top of this Privacy Policy.
          </p>
          <p>
            It is recommended that you examine this Privacy policy periodically for modifications. Changes to this Privacy policy become effective upon their publication on this page.
          </p>
        </div>

        <div className="privacy-section mb-5">
          <h2 className="section-title mb-3">25. How to Contact Us</h2>
          <p>
            Please contact us via email at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> if you have any queries regarding this Privacy policy.
          </p>
        </div>

      </LegalLayout>
    </div>
  );
};

export default Privacy;